import React, { useState, useEffect, useCallback } from "react";
import useApi from "../../Hooks/useApi";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus, FiEdit, FiTrash2, FiSmile, FiGift } from "react-icons/fi";
import { FaHourglassHalf } from "react-icons/fa";
import useNotification from "../../Components/Notification";
import translations from "./translations.json";
import "./css/fullScreenNotifications.css";

// --- helpers to render arrays/objects safely ---
const safeStringify = (obj) => {
  try {
    const seen = new WeakSet();
    return JSON.stringify(obj, (k, v) => {
      if (typeof v === "object" && v !== null) {
        if (seen.has(v)) return "[Circular]";
        seen.add(v);
      }
      return v;
    });
  } catch {
    return Object.prototype.toString.call(obj);
  }
};

const labelFromItem = (item) => {
  if (item == null) return "";
  if (
    typeof item === "string" ||
    typeof item === "number" ||
    typeof item === "bigint"
  )
    return String(item);
  if (item instanceof Date) return item.toLocaleString();
  if (typeof item === "object") {
    // Prefer common label-ish keys
    const keys = ["name", "title", "label", "message", "email", "id", "_id"];
    for (const k of keys) {
      if (item[k] != null && item[k] !== "") return String(item[k]);
    }
    return safeStringify(item);
  }
  try {
    return String(item);
  } catch {
    return "";
  }
};

const toText = (val) => {
  if (val == null) return "";
  if (Array.isArray(val)) return val.map(labelFromItem).join(", ");
  if (typeof val === "object") return labelFromItem(val);
  return String(val);
};
// --- end helpers ---

const FullScreenNotifications = ({ isOpen, onClose }) => {
  const api = useApi();
  const lang = localStorage.getItem("preferredLanguage") || "nl";
  const t = translations[lang].fullScreenNotifications;

  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const { triggerNotification, NotificationComponent } = useNotification();

  const [showSwipeInstruction, setShowSwipeInstruction] = useState(
    () => localStorage.getItem("swipeInstructionSeen") !== "true"
  );

  useEffect(() => {
    const stored = localStorage.getItem("dismissedNotifications");
    if (stored) setDismissed(JSON.parse(stored));
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await api.get(`${window.baseDomain}api/notifications`, {
        noCache: true,
      });
      if (data) setNotifications(data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [api]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // Listen for Socket.IO notification events via custom event
  useEffect(() => {
    const handleNotificationCreated = (event) => {
      console.log("🔔 [FullScreenNotifications] New notification received", event.detail);
      fetchNotifications();
    };

    window.addEventListener("redux:notifications/created", handleNotificationCreated);

    return () => {
      window.removeEventListener("redux:notifications/created", handleNotificationCreated);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    localStorage.setItem("dismissedNotifications", JSON.stringify(dismissed));
  }, [dismissed]);

  const visibleNotifications = notifications.filter((n) => {
    const id = n._id || n.reservationId;
    return !dismissed.includes(id);
  });

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2 } },
  };
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    exit: { opacity: 0, x: 150, transition: { duration: 0.4, ease: "easeIn" } },
  };

  const dismissNotification = (id) => {
    setDismissed((prev) => [...prev, id]);
    triggerNotification(t.hiddenNotification, "info");
  };

  const handleDragEnd = (e, info, id) => {
    if (info.offset.x > 150) dismissNotification(id);
  };

  const grouped = {};
  visibleNotifications.forEach((n) => {
    const key = n.currentDate;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(n);
  });
  const sortedDates = Object.keys(grouped).sort((a, b) => {
    const [da, ma, ya] = a.split("-").map(Number);
    const [db, mb, yb] = b.split("-").map(Number);
    return new Date(yb, mb - 1, db) - new Date(ya, ma - 1, da);
  });

  const getEventStyle = (event) => {
    switch (event) {
      case "creation":
        return { color: t.eventColors.creation, icon: <FiPlus /> };
      case "update":
        return { color: t.eventColors.update, icon: <FiEdit /> };
      case "deletion":
        return { color: t.eventColors.deletion, icon: <FiTrash2 /> };
      case "giftcard_purchase":
        return { color: t.eventColors.giftcard, icon: <FiGift /> };
      default:
        return { color: t.eventColors.default, icon: <FiSmile /> };
    }
  };

  const formatDateDutch = (currentDate) => {
    const [day, month, year] = currentDate.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(
      lang === "fr" ? "fr-FR" : lang === "en" ? "en-GB" : "nl-NL",
      { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    );
  };

  const handleCloseSwipeInstruction = (e) => {
    e.stopPropagation();
    setShowSwipeInstruction(false);
    localStorage.setItem("swipeInstructionSeen", "true");
  };

  const handleClose = async (e) => {
    if (e) e.stopPropagation();
    const unread = notifications.filter((n) => !n.read);
    if (unread.length) {
      const ids = unread.map((n) => n._id || n.reservationId);
      try {
        const res = await api.patch(`${window.baseDomain}api/notifications`, {
          notificationIds: ids,
        });
        if (res?.modifiedCount >= 0) {
          setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        }
      } catch (err) {
        console.error("Error marking read:", err);
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fullscreen-notifications-page fullscreen-notifications-backdrop"
      onClick={handleClose}
    >
      <button className="close-fullscreen-button" onClick={handleClose}>
        ×
      </button>

      <div
        className="notifications-scroll-container"
        onClick={(e) => e.stopPropagation()}
      >
        <AnimatePresence>
          {sortedDates.map((dateKey) => (
            <motion.div
              key={dateKey}
              className="notification-group"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <h2 className="group-date">{formatDateDutch(dateKey)}</h2>

              {grouped[dateKey]
                .sort(
                  (a, b) =>
                    new Date(b.creationTimestamp) -
                    new Date(a.creationTimestamp)
                )
                .map((notification) => {
                  const id = notification._id || notification.reservationId;
                  const { color, icon } = getEventStyle(notification.event);
                  const isGift = notification.event === "giftcard_purchase";
                  const isWaitlist = !isGift && !notification.guests;

                  return (
                    <motion.div
                      key={id}
                      className="notification-card"
                      variants={cardVariants}
                      drag="x"
                      dragConstraints={{ left: 0, right: 300 }}
                      onDragEnd={(e, info) => handleDragEnd(e, info, id)}
                    >
                      <div
                        className="notification-card-left"
                        style={{ backgroundColor: String(color || "") }}
                      >
                        {notification.guests ? (
                          <div className="notification-card-left-content">
                            <span
                              className="guest-number"
                              translate="no"
                              style={{ fontWeight: "bold" }}
                            >
                              {String(notification.guests)} p
                            </span>
                            <span
                              className="notification-icon"
                              style={{ fontSize: "24px" }}
                            >
                              {icon}
                            </span>
                          </div>
                        ) : (
                          <span
                            className="notification-icon"
                            style={{ fontSize: "24px" }}
                          >
                            {isWaitlist ? <FaHourglassHalf /> : icon}
                          </span>
                        )}
                      </div>

                      <div className="notification-card-content">
                        <h4 className="notification-title">
                          {toText(t.eventPrefixes[notification.event]) || ""}
                          {isGift ? (
                            <>
                              {notification.giftcardAmount != null && (
                                <span className="giftcard-amount">
                                  {toText(notification.giftcardAmount)}
                                </span>
                              )}
                              {(notification.firstName ||
                                notification.lastName) && (
                                <span>
                                  {" "}
                                  {t.by}{" "}
                                  <span translate="no">
                                    {toText(notification.firstName) || ""}{" "}
                                    {toText(notification.lastName) || ""}
                                  </span>
                                </span>
                              )}
                              {(notification.recipientFirstName ||
                                notification.recipientLastName) && (
                                <span>
                                  {" "}
                                  {t.for}{" "}
                                  <span translate="no">
                                    {toText(notification.recipientFirstName) ||
                                      ""}{" "}
                                    {toText(notification.recipientLastName) ||
                                      ""}
                                  </span>
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {notification.oldGuests &&
                              notification.newGuests ? (
                                <span
                                  className="guests-transition"
                                  translate="no"
                                >
                                  {toText(notification.oldGuests)} p →{" "}
                                  {toText(notification.newGuests)} p
                                </span>
                              ) : notification.guests ? (
                                `${String(notification.guests)} p`
                              ) : (
                                ""
                              )}
                              <span translate="no">
                                {notification.date &&
                                  ` ${t.on} ${toText(notification.date)}`}
                                {notification.time &&
                                  ` ${t.at} ${toText(notification.time)}`}
                              </span>
                              <span translate="no">
                                {(notification.firstName ||
                                  notification.lastName) &&
                                  ` - ${toText(notification.firstName) || ""} ${
                                    toText(notification.lastName) || ""
                                  }`}
                              </span>
                            </>
                          )}
                          {notification.reservationType === "auth" && (
                            <span className="auth-tag">{t.tags.admin}</span>
                          )}
                          {notification.reservationType === "giftcard" && (
                            <span className="giftcard-tag">
                              {t.tags.giftcardTag}
                            </span>
                          )}
                          {isWaitlist && (
                            <span className="waitlist-tag">
                              {t.tags.waitlist}
                            </span>
                          )}
                        </h4>

                        <p className="notification-details">
                          {notification.menu != null && (
                            <span className="menu-text" translate="no">
                              {toText(notification.menu)}
                            </span>
                          )}
                          {notification.email != null && (
                            <span className="email-text" translate="no">
                              {notification.menu ? " | " : ""}
                              {toText(notification.email)}
                            </span>
                          )}
                          {isGift && notification.recipientEmail && (
                            <span className="recipient-email">
                              {t.recipient}:{" "}
                              <span translate="no">
                                {toText(notification.recipientEmail)}
                              </span>
                            </span>
                          )}
                        </p>
                      </div>

                      {!notification.read && <div className="unread-dot"></div>}
                    </motion.div>
                  );
                })}
            </motion.div>
          ))}
        </AnimatePresence>

        {dismissed.length > 0 && (
          <button
            className="restore-hidden-button button-style-1"
            onClick={(e) => {
              e.stopPropagation();
              setDismissed([]);
              localStorage.removeItem("dismissedNotifications");
            }}
          >
            {t.restoreButton}
          </button>
        )}
      </div>

      <div className="bottom-gradient"></div>
      <NotificationComponent />

      {showSwipeInstruction && (
        <div className="swipe-instruction">
          <button
            className="swipe-instruction-close"
            onClick={handleCloseSwipeInstruction}
          >
            ×
          </button>
          <h4 className="swipe-instruction-title">{t.swipeTitle}</h4>
          <p className="swipe-instruction-text">{t.swipeText}</p>
        </div>
      )}
    </div>
  );
};

export default FullScreenNotifications;
